import { DataTable, DataTableRowClickEvent } from 'primereact/datatable';
import { Column } from 'primereact/column';
import React from 'react';
import {
  Row,
  Col,
  Button,
  Form,
  Label,
  Input,
  Card,
  CardBody,
  CardHeader,
} from 'reactstrap';
import { LinkedComponent } from 'valuelink';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { RouteComponentProps } from 'react-router';
import {
  Merchant,
  MerchantStoreInfo,
} from './MerchantComponents/MerchantModel';
import i18n, { getDateFormatOptions } from '@/utils/i18n';
import { InventoryCheck } from './InventoryCheckView';
import { Communicator } from '../tools/Communication';
import { ContentSection } from '../components/Layout/ContentSection';
import { Routes } from '@/utils/routes';

interface InventoryCheckListViewProps extends RouteComponentProps<any> {}

interface IdName {
  id: number;
  name: string;
}

interface InventoryCheckSearchResult {
  offset: number;
  totalCount: number;
  inventoryChecks: InventoryCheck[];
}

interface InventoryCheckListViewState {
  isLoading: boolean;
  isSubmitting: boolean;

  inventoryChecks: InventoryCheck[];
  selection: InventoryCheck[];
  totalCount: number;
  listOffset: number;
  listLimit: number;

  searchStoreIdEnabled: boolean;
  selectedMerchantId: string;
  selectedStoreId: string;
  searchBillingPeriodEnabled: boolean;
  searchMonth: number; // 0-11
  searchYear: number;

  years: number[];

  merchants: Merchant[];
  selectedMerchant: Merchant | null;
  selectedStore: MerchantStoreInfo | null;
  containerTypes: IdName[];
}

const defaultItem = { id: '0', name: 'undefined' };

export class InventoryCheckListView extends LinkedComponent<
  InventoryCheckListViewProps,
  InventoryCheckListViewState
> {
  constructor(props: InventoryCheckListViewProps) {
    super(props);
    const now = new Date(Date.now());
    const yearsAmount = now.getUTCFullYear() - 2018;
    const urlParams = new URLSearchParams(this.props.location.search);
    this.state = {
      isLoading: true,
      isSubmitting: false,

      inventoryChecks: [],
      selection: [],
      totalCount: 0,
      listOffset: 0,
      listLimit: 25,

      searchStoreIdEnabled: urlParams.get('searchStoreId') === 'true',
      selectedMerchantId: urlParams.get('merchantId') || '',
      selectedStoreId: urlParams.get('storeId') || '',
      searchBillingPeriodEnabled:
        urlParams.get('searchBillingPeriod') === 'true',
      searchMonth: parseInt(
        urlParams.get('month') || now.getUTCMonth().toString(),
      ),
      searchYear: parseInt(
        urlParams.get('year') || now.getUTCFullYear().toString(),
      ),

      years: Array.from({ length: yearsAmount }, (_, i) => 2019 + i),
      merchants: [],
      selectedMerchant: null,
      selectedStore: null,
      containerTypes: [],
    };
  }

  updateMerchants() {
    return Communicator.Get('/Merchant/GetAllMerchantsWithStores', {})
      .then((response) => response.json())
      .then((data: { merchants: Merchant[] }) => {
        const merchant: Merchant | null =
          data.merchants.find((m) => m.id == this.state.selectedMerchantId) ||
          null;
        const store: MerchantStoreInfo | null = merchant
          ? merchant.stores.find((m) => m.id == this.state.selectedStoreId) ||
            null
          : null;

        const sortedMerchants = [...data.merchants].sort(function (a, b) {
          if (a.name < b.name) {
            return -1;
          }
          if (a.name > b.name) {
            return 1;
          }
          return 0;
        });

        this.setState({
          merchants: sortedMerchants,
          selectedMerchant: merchant,
          selectedStore: store,
        });
        this.updateData(null, false);
      });
  }

  updateData(listOffset: number | null = null, setLoading = true) {
    if (listOffset === null) {
      listOffset = this.state.listOffset;
    } else {
      this.setState({ listOffset: listOffset });
    }

    let constraints = [];

    if (this.state.searchStoreIdEnabled && this.state.selectedStore != null) {
      constraints.push({
        type: 'StoreIdConstraint',
        data: JSON.stringify({ storeId: this.state.selectedStore.id }),
      });
    }
    if (this.state.searchBillingPeriodEnabled) {
      constraints.push({
        type: 'BillingPeriodConstraint',
        data: JSON.stringify({
          year: this.state.searchYear,
          month: this.state.searchMonth,
        }),
      });
    }

    if (setLoading) {
      this.setState({ isLoading: true });
    }

    let paramsObj = {
      searchStoreId: this.state.searchStoreIdEnabled.toString(),
      merchantId: this.state.selectedMerchantId.toString(),
      storeId: this.state.selectedStoreId.toString(),
      searchBillingPeriod: this.state.searchBillingPeriodEnabled.toString(),
      year: this.state.searchYear.toString(),
      month: this.state.searchMonth.toString(),
    };

    const params = new URLSearchParams(paramsObj).toString();
    this.props.history.push(`${Routes.inventoryChecks()}?${params}`);

    Communicator.PostData('api/3/InventoryCheck/SearchInventoryChecks', {
      offset: listOffset,
      limit: this.state.listLimit,
      constraints: constraints,
    })
      .then((response) => response.json())
      .then((data: InventoryCheckSearchResult) => {
        this.setState({
          totalCount: data.totalCount,
          inventoryChecks: data.inventoryChecks,
          selection: [],
          isLoading: setLoading ? false : this.state.isLoading,
        });
      });
  }

  componentDidMount() {
    const promises = [
      // this.updateData is called inside updateMerchants
      this.updateMerchants(),
      this.reloadContainerTypes(),
    ];
    Promise.all(promises).then(() => this.setState({ isLoading: false }));
  }

  reloadContainerTypes() {
    Communicator.Get('/Container/GetAllContainerCategories', {})
      .then((response) => response.json())
      .then((data: { containerTypes: IdName[] }) => {
        if (data.containerTypes) {
          // assume that container types have ids from 1 to n without 'holes'
          this.setState({
            containerTypes: [...data.containerTypes].sort(
              (a, b) => a.id - b.id,
            ),
          });
        }
      });
  }

  handleRowClick(row: DataTableRowClickEvent) {
    const win = window.open(`/inventory-check/${row.data.id}`, '_blank');
    win && win.focus();
  }

  getStoresForSelectedMerchant() {
    if (this.state.selectedMerchant != null) {
      return this.state.selectedMerchant.stores.map((_) => (
        <option key={'sto' + _.id} value={_.id}>
          {_.name}
        </option>
      ));
    }
    return null;
  }

  getUsedContainerTypesString(
    usedContainerTypeNames: string[],
    usedContainerTypes: number[],
  ): any {
    if (usedContainerTypeNames.length > 3) {
      return usedContainerTypes.join(', ');
    }
    return usedContainerTypeNames.join(', ');
  }

  renderCell(content: any, displayAsAnchor = false, alias = '') {
    if (displayAsAnchor) {
      return (
        <a style={{ wordBreak: 'break-word', display: 'block' }} href={content}>
          <span>{alias}</span>
        </a>
      );
    }
    return (
      <div style={{ wordBreak: 'break-word', display: 'block' }}>
        <span>{content}</span>
      </div>
    );
  }

  render() {
    const linked = this.linkAll();
    return (
      <ContentSection
        title={i18n.t('InventoryCheck:search')}
        collapsable={false}
      >
        <Row>
          <Col md="6">
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                this.updateData();
                return false;
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  this.updateData();
                  return false;
                }
                return true;
              }}
            >
              {/* This disabled submit button disables any implicit form submit */}
              <button
                type="submit"
                disabled
                style={{ display: 'none' }}
                aria-hidden="true"
              />

              <Card className="mb-2">
                <CardHeader className="ps-4 pt-1 pb-0">
                  <Input
                    type="checkbox"
                    name="searchContainerStoreIdEnabled"
                    id="searchContainerStoreIdEnabled"
                    checked={linked.searchStoreIdEnabled.props.checked}
                    onChange={linked.searchStoreIdEnabled.props.onChange}
                    style={{ WebkitAppearance: 'checkbox' }}
                  />
                  <Label for="searchContainerStoreIdEnabled">
                    {i18n.t('InventoryCheck:storeFilterInfo')}
                  </Label>
                </CardHeader>
                {this.state.searchStoreIdEnabled ? (
                  <CardBody>
                    <div className="row">
                      <div className="col-6">
                        <Input
                          type="select"
                          value={
                            this.state.selectedMerchantId
                              ? this.state.selectedMerchantId
                              : ''
                          }
                          style={{ WebkitAppearance: 'listbox' }}
                          onChange={(e) => {
                            let selectedStore: MerchantStoreInfo | null = null;
                            let selectedMerchant =
                              this.state.merchants.find(
                                (m) => m.id == e.target.value,
                              ) || null;
                            if (
                              selectedMerchant != null &&
                              selectedMerchant.stores.length == 1
                            ) {
                              // If the merchant has only one store, select that store by default
                              selectedStore = selectedMerchant.stores[0];
                            }
                            this.setState({
                              selectedMerchant: selectedMerchant,
                              selectedStore: selectedStore,
                              selectedMerchantId: selectedMerchant?.id || '',
                              selectedStoreId: selectedStore?.id || '',
                            });
                          }}
                        >
                          <option disabled value="">
                            {i18n.t('InventoryCheck:selectMerchant')}
                          </option>
                          {this.state.merchants.map((_) => (
                            <option key={'mer' + _.id} value={_.id}>
                              {_.name}
                            </option>
                          ))}
                        </Input>
                      </div>
                      <div className="col-6">
                        <Input
                          type="select"
                          disabled={!this.state.selectedMerchantId}
                          value={this.state.selectedStoreId}
                          style={{ WebkitAppearance: 'listbox' }}
                          onChange={(e) => {
                            let selectedStore = this.state.selectedMerchant
                              ? this.state.selectedMerchant.stores.find(
                                  (m) => m.id == e.target.value,
                                ) || null
                              : null;
                            this.setState({
                              selectedStore: selectedStore,
                              selectedStoreId: selectedStore?.id || '',
                            });
                          }}
                        >
                          <option disabled value="">
                            {i18n.t('InventoryCheck:selectStore')}
                          </option>
                          {this.getStoresForSelectedMerchant()}
                        </Input>
                      </div>
                    </div>
                  </CardBody>
                ) : null}
              </Card>
              <Card className="mb-2">
                <CardHeader className="ps-4 pt-1 pb-0">
                  <Input
                    type="checkbox"
                    name="searchBillingPeriodEnabled"
                    id="searchBillingPeriodEnabled"
                    checked={linked.searchBillingPeriodEnabled.props.checked}
                    onChange={linked.searchBillingPeriodEnabled.props.onChange}
                    style={{ WebkitAppearance: 'checkbox' }}
                  />
                  <Label for="searchBillingPeriodEnabled">
                    {i18n.t('InventoryCheck:searchBillingPeriodInfo')}
                  </Label>
                </CardHeader>
                {this.state.searchBillingPeriodEnabled ? (
                  <CardBody>
                    <div className="row">
                      <div className="col-6">
                        <select
                          id="searchMonth"
                          name="searchMonth"
                          className="form-control"
                          disabled={!this.state.searchBillingPeriodEnabled}
                          style={{ WebkitAppearance: 'listbox' }}
                          {...linked.searchMonth.props}
                        >
                          {Array.from({ length: 12 }, (_, i) => (
                            <option key={i} value={i}>
                              {i18n.t(`Common:Month:${i + 1}`)}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-6">
                        <select
                          id="searchYear"
                          name="searchYear"
                          className="form-control"
                          disabled={!this.state.searchBillingPeriodEnabled}
                          style={{ WebkitAppearance: 'listbox' }}
                          {...linked.searchYear.props}
                        >
                          {this.state.years.map((i) => (
                            <option key={i} value={i}>
                              {i}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </CardBody>
                ) : null}
              </Card>

              <Button onClick={() => this.updateData()} color="primary" block>
                {i18n.t('InventoryCheck:search')}
              </Button>
            </Form>
          </Col>
        </Row>
        <hr />
        <p>
          {i18n.t('InventoryCheck:inventoryCheckListInfo', {
            current: this.state.inventoryChecks.length || 0,
            total: this.state.totalCount || 0,
          })}
        </p>
        <DataTable
          value={this.state.inventoryChecks}
          selection={this.state.selection}
          selectionMode="multiple"
          onSelectionChange={(e) => this.setState({ selection: e.value })}
          dataKey="id"
          lazy
          paginator
          loading={this.state.isLoading || this.state.isSubmitting}
          totalRecords={this.state.totalCount}
          rows={this.state.listLimit}
          first={this.state.listOffset}
          onPage={(e) => {
            this.updateData(e.first);
          }}
          onRowClick={(row) => this.handleRowClick(row)}
          className="p-datatable-striped"
          style={{ width: '100%', overflowX: 'scroll' }}
        >
          <Column
            headerStyle={{ width: '100px' }}
            body={(i: InventoryCheck) =>
              this.renderCell(
                `/inventory-check/${i.id}`,
                true,
                i18n.t('Common:view'),
              )
            }
            header={i18n.t('Common:inventoryCheck')}
          />
          <Column
            headerStyle={{ width: '100px' }}
            body={(i: InventoryCheck) =>
              this.renderCell(`/merchants/null/${i.storeId}`, true, i.storeName)
            }
            header={i18n.t('Common:store')}
          />
          <Column
            headerStyle={{ width: '100' }}
            body={(i: InventoryCheck) =>
              this.renderCell(
                new Date(i.createdAt).toLocaleDateString(
                  i18n.language,
                  getDateFormatOptions(true),
                ),
              )
            }
            header={i18n.t('InventoryCheck:createdAt')}
          />
          <Column
            headerStyle={{ width: '100' }}
            body={(i: InventoryCheck) =>
              this.renderCell(
                new Date(i.expirationDate).toLocaleDateString(
                  i18n.language,
                  getDateFormatOptions(false),
                ),
              )
            }
            header={i18n.t('InventoryCheck:expirationDate')}
          />
          <Column
            headerStyle={{ width: '125px' }}
            body={(i: InventoryCheck) =>
              this.renderCell(i18n.t(`InventoryCheck:Status:${i.status}`))
            }
            header={i18n.t('Common:status')}
          />
          <Column
            headerStyle={{ width: '50' }}
            body={(i: InventoryCheck) => this.renderCell(i.missingCount)}
            header={i18n.t('InventoryCheck:missingContainers')}
          />
          <Column
            headerStyle={{ width: '50px' }}
            body={(i: InventoryCheck) => this.renderCell(i.recoveredCount)}
            header={i18n.t('InventoryCheck:recoveredContainers')}
          />
          <Column
            headerStyle={{ width: '50px' }}
            body={(i: InventoryCheck) => this.renderCell(i.missingPartsCount)}
            header={i18n.t('InventoryCheck:missingParts')}
          />
          <Column
            headerStyle={{ width: '200px' }}
            body={(i: InventoryCheck) =>
              this.renderCell(i.discountPercentage || 0)
            }
            header={i18n.t('InventoryCheck:discountPercentage')}
          />
        </DataTable>
      </ContentSection>
    );
  }
}
